import React, {useState, useEffect} from 'react';
import axios from 'axios';
import AdminMenu from "../AdminMenu";
import AdminHeader from "../AdminHeader";
import Button from "../../../components/Button";
import InputField from "../../../components/InputField";
import ImageUpload from "../../../components/ImageUpload";
import TextArea from "../../../components/TextArea";
import TagsInput from "../../../components/TagsInput";
import ErrorMessage from "../../../components/ErrorMessage";
import {useNavigate, useParams} from 'react-router-dom';
import { ADMIN_API_BASE } from "../../../utils/apiConfig";

const CourseOverview = () => {
    const {id: urlId} = useParams();        // ممکنه "new" باشه یا id واقعی
    const navigate = useNavigate();

    const isNewCourse = urlId === 'new';      // ← تشخیص حالت جدید
    const realCourseId = isNewCourse ? null : urlId;

    const [course, setCourse] = useState({
        id: realCourseId,
        price: 0,
        paid_levels: 0,
        title: '',
        category: '',
        description: '',
        difficulty: 0,
        prerequisites: [],
        syllabus: [],
        image: '',
        is_active: false
    });

    const [message, setMessage] = useState({type: '', text: ''});
    const [courseId, setCourseId] = useState(realCourseId);

    const getCsrfToken = async () => {
        const res = await fetch(`${ADMIN_API_BASE}/csrf-token`, {
            credentials: 'include'
        });
        const data = await res.json();
        return data.csrfToken;
    };

    useEffect(() => {
        if (realCourseId) {
            const load = async () => {
                try {
                    const token = await getCsrfToken();
                    const res = await axios.get(`${ADMIN_API_BASE}/course/${realCourseId}`, {
                        headers: {'csrf-token': token},
                        withCredentials: true
                    });
                    if (res.data.draft) {
                        setCourse(res.data.draft);
                        setCourseId(realCourseId);
                    }
                } catch (err) {
                    setMessage({type: 'error', text: 'دوره یافت نشد'});
                }
            };
            load();
        }
    }, [realCourseId]);

    const handleChange = (name, value) => {
        setCourse(prev => ({...prev, [name]: value}));
    };

    const handleChangeId = (id) => {
        setCourseId(prev => (id === prev ? null : id));
    };

    const handleImageChange = (file) => {
        setCourse(prev => ({...prev, image: file}));
    };

    const saveCourse = async () => {
        const formData = new FormData();

        // Ensure `id` is sent for new course
        if (!courseId) {
            // generate a random 8-16 char ID or use input field value
            const newId = Math.random().toString(36).substring(2, 10); // example random id
            formData.append('id', newId);
            setCourseId(newId);
        } else {
            formData.append('id', courseId);
        }

        Object.entries(course).forEach(([key, value]) => {
            if (value === undefined || value === null || value === "") return;

            if (Array.isArray(value)) {
                formData.append(key, JSON.stringify(value));
            } else {
                formData.append(key, value);
            }
        });

        if (course.image && course.image instanceof File) {
            formData.append("image", course.image);
        }

        try {
            const token = await getCsrfToken();
            let res;

            if (courseId !== 'new' && courseId != null && courseId !== '') {
                // Update
                res = await axios.put(`${ADMIN_API_BASE}/course/${courseId}`, formData, {
                    headers: { 'csrf-token': token },
                    withCredentials: true
                });
            } else {
                // Create
                res = await axios.post(`${ADMIN_API_BASE}/course/`, formData, {
                    headers: { 'csrf-token': token },
                    withCredentials: true
                });

                if (res.data.id) {
                    setCourseId(res.data.id);
                    navigate(`/admin/course/${res.data.id}/overview/`);
                }
            }

            if (res.data.image) {
                setCourse(prev => ({ ...prev, image: res.data.image }));
            }

            setMessage({ type: 'success', text: 'دوره با موفقیت ذخیره شد!' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || err.message });
        }
    };

    return (
        <div className="flex flex-1">
            <div className="w-1/5">
                <AdminMenu path="./"/>
            </div>
            <div className="flex-grow w-4/5">
                <AdminHeader title={isNewCourse ? 'دوره‌ی جدید' : 'ویرایش دوره'}/>

                {message.text && <ErrorMessage type={message.type} message={message.text}/>}

                <form onSubmit={e => e.preventDefault()} className="space-y-6 max-w-2xl">

                    <InputField title="ID" value={courseId} onChange={e => handleChangeId(e.target.value)} />

                    <InputField title="عنوان دوره" value={course.title}
                                onChange={e => handleChange('title', e.target.value)}/>

                    <InputField title="دسته‌بندی" value={course.category}
                                onChange={e => handleChange('category', e.target.value)}/>

                    <InputField title="قیمت (تومان)" type="number" value={course.price}
                                onChange={e => handleChange('price', +e.target.value)}/>

                    <InputField title="سطح پولی از" type="number" value={course.paid_levels}
                                onChange={e => handleChange('paid_levels', +e.target.value)}/>

                    <InputField title="سختی" value={course.difficulty} type="number"
                                onChange={e => handleChange('difficulty', e.target.value)}/>

                    <div>
                        <label className="block font-bold text-gray-700 mb-2">تصویر دوره</label>
                        <ImageUpload
                            existingImage={typeof course.image === 'string' ? course.image : null}
                            onFileChange={handleImageChange}
                        />
                    </div>

                    <TextArea title="توضیحات دوره" value={course.description}
                              onChange={e => handleChange('description', e.target.value)}/>

                    <TagsInput title="پیش‌نیازها" value={course.prerequisites}
                               onChange={val => handleChange('prerequisites', val)}/>

                    <TagsInput title="سرفصل‌ها" value={course.syllabus}
                               onChange={val => handleChange('syllabus', val)}/>

                    <div className="flex items-center gap-3">
                        <input type="checkbox" checked={course.is_active}
                               onChange={e => handleChange('is_active', e.target.checked)}/>
                        <label>دوره فعال باشد</label>
                    </div>

                    <Button onClick={saveCourse} className="bg-green-600 text-white px-8 py-3">
                        {isNewCourse ? 'ایجاد دوره' : 'بروزرسانی دوره'}
                    </Button>
                </form>
            </div>
        </div>
    );
};

export default CourseOverview;
